import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AppProvider, useAppContext } from './context/AppContext';
import { ThemeContextProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import LandingPage from './components/LandingPage';
import Results from './components/Results';
import About from './components/About';
import Contact from './components/Contact';
import Methodology from './components/Methodology';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import VibeCheck from './components/vibecheck/VibeCheck';
import PageTransition from './components/PageTransition';
import { ErrorBoundary } from './utils/errorBoundary';
import { validateEnvironment } from './config/environment';

// Validate environment on app startup
validateEnvironment();

const AppRoutes: React.FC = () => {
  const { audioFeatures, isAnalyzing, error } = useAppContext();
  const location = useLocation();

  return (
    <Layout>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route 
            path="/" 
            element={<PageTransition><LandingPage /></PageTransition>} 
          />
          {/* /results/:playlistId is the shareable, re-hydratable route. Bare
              /results still works for an in-flight/just-finished session; when it
              has no data and no id to hydrate from, it falls back to landing. */}
          <Route
            path="/results"
            element={
              audioFeatures.length > 0 || isAnalyzing || error ? (
                <PageTransition><Results /></PageTransition>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/results/:playlistId"
            element={<PageTransition><Results /></PageTransition>}
          />
          <Route path="/vibecheck" element={<PageTransition><VibeCheck /></PageTransition>} />
          <Route path="/about" element={<PageTransition><About /></PageTransition>} />
          <Route path="/methodology" element={<PageTransition><Methodology /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
          <Route path="/terms" element={<PageTransition><Terms /></PageTransition>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeContextProvider>
        <Router>
          <AppProvider>
            <AppRoutes />
          </AppProvider>
        </Router>
      </ThemeContextProvider>
    </ErrorBoundary>
  );
};

export default App;
